import { useState } from 'react';
import { sanitize as sanitizeHtml } from 'isomorphic-dompurify';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useScholarshipTestInfo from '@api/hooks/scholarship/useScholarshipTestInfo';

const Wrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-bottom: 80px;
`;

const Heading = styled.div`
  text-align: center;
  h2 {
    margin-bottom: ${Spacings.SPACING_3B};
  }
  p {
    font-size: ${Fonts.LARGE};
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const QuestionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_4B};
  }
`;

const QuestionWrapper = styled.div`
  width: 48%;
  height: ${({ $faqOpen }) => ($faqOpen ? 'max-content' : '112px')};
  padding: ${Spacings.SPACING_8B};
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  margin-right: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 100%;
    margin-right: 0;
  }
`;

const Question = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const Answer = styled(P1)`
  display: ${({ $faqOpen }) => ($faqOpen ? 'block' : 'none')};
  margin-top: ${Spacings.SPACING_6B};
  /* transition: display 400ms ease-in-out; */
`;

const ArrowDown = styled(KeyboardArrowDown)`
  transition: transform 400ms ease-in-out;
  flex-shrink: 0;
  transform: ${({ $faqOpen }) =>
    $faqOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FaqIcon = styled.img`
  height: 80px;
  width: 80px;
  margin-bottom: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: 64px;
    width: 64px;
  }
`;

const FAQ = () => {
  const {
    query: { eventKey }
  } = useRouter();
  const { prodigyInfo } = useScholarshipTestInfo(eventKey);

  const { faqMeta, faq } = prodigyInfo;

  const [faqOpen, setFaqOpen] = useState(null);

  return (
    <Wrapper>
      {faqMeta && (
        <Heading>
          <FaqIcon src={faqMeta.image} alt="faqs" />
          <H2>{faqMeta.title}</H2>
          <P1
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(faqMeta.subTitle || '')
            }}
          />
        </Heading>
      )}
      <QuestionsWrapper>
        {faq.map((question) => {
          const isOpen = faqOpen === question.id;
          return (
            <QuestionWrapper
              key={question.id}
              onClick={() => setFaqOpen(faqOpen ? null : question.id)}
              $faqOpen={isOpen}
            >
              <Question>
                <H5>{question.question}</H5>
                <ArrowDown size="24px" $faqOpen={isOpen} />
              </Question>
              <Answer
                $faqOpen={isOpen}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(question.answer || '')
                }}
              />
            </QuestionWrapper>
          );
        })}
      </QuestionsWrapper>
    </Wrapper>
  );
};

export default FAQ;
